<script lang="ts">
	import type { FontProps } from '$types/fontProps'
	import { DefaultRobotoFontProps, getFontPropsVarString } from '$types/fontProps'
	import type { Snippet } from 'svelte'
	import { SvelteMap } from 'svelte/reactivity'
	import type { NodeData } from '$types/nodeData'
	import Self from './TreeView.svelte'

	interface Props {
		// this is basically AT LEAST a list of NodeData
		nodes: NodeData[]
		activeNode: NodeData
		fontProps?: FontProps
		// vertical padding applied to each element top AND bottom in REM
		vElementPadding?: number
		indentation?: string
		treeExpansionMapKey?: string
		actualDepth?: number
		/**
		 * expansion state map passed in from the parent (was previously a global store).
		 * a reactive SvelteMap is expected here so immutable .set() updates trigger a re-render.
		 */
		expansionStates?: SvelteMap<string, string[]>
		// disables clicks in the tree to navigate
		navigationDisabled?: boolean
		// fired when a node label is clicked; receives the clicked node directly
		onLabelClick?: (selectedMaterial: NodeData) => void
		// per-node action markup (forwarded through the recursive instance)
		nodeAction?: Snippet<[NodeData]>
		// per-node data markup (forwarded through the recursive instance)
		nodeData?: Snippet<[NodeData]>
		/**
	 * Overrides the entire node render for each tree node when provided. Built-in
	 * descendant recursion is bypassed — callers must render child nodes themselves
	 * (e.g. via a nested Self / TreeView). Does not apply to multi-level trees unless
	 * the snippet explicitly handles recursion.
	 */
	children?: Snippet<[NodeData]>
	}

	let {
		nodes,
		activeNode,
		fontProps = DefaultRobotoFontProps('18px', '0.75rem'),
		vElementPadding = 1,
		indentation = '1.5rem',
		treeExpansionMapKey = 'mmpv',
		actualDepth = 0,
		expansionStates = new SvelteMap(),
		navigationDisabled = false,
		onLabelClick,
		nodeAction,
		nodeData,
		children
	}: Props = $props()

	function toggleExpansion(id: string, leaf: boolean) {
		if (!leaf && !navigationDisabled) {
			// immutable update: replace the array so the reactive SvelteMap fires
			const current = expansionStates.get(treeExpansionMapKey) ?? []
			if (current.includes(id)) {
				expansionStates.set(
					treeExpansionMapKey,
					current.filter((expandedId) => expandedId !== id)
				)
			} else {
				expansionStates.set(treeExpansionMapKey, [...current, id])
			}
		}
	}

	function findChildrenAndAllOfLevelsBelow(node: NodeData) {
		return nodes
			.filter(
				(n) =>
					(node.childrenIds.includes(n.id) && n.parentId === node.id) || n.level > actualDepth + 1
			)
			.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0))
	}

	let actualLevelNodes: NodeData[] = $derived(
		nodes !== null && nodes.length !== 0
			? nodes
					.filter((n) => n.level === actualDepth)
					.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0))
			: []
	)
	// x-position of the lines
	let linePosition: number = $derived(parseFloat(indentation) / 2 + 0.2)
	// line-height in rem extracted as number
	let lineHeight: number = $derived(parseFloat(fontProps.lineHeight))
	let itemHeight: number = $derived(2 * vElementPadding + (lineHeight / 16) * 3)
	let styleString: string = $derived(`
        --itemHeight:${itemHeight}rem;
        --horizontal-line-pos-y:${itemHeight / 2}rem;
        --vPadding:${vElementPadding}rem;
        --indentation: ${indentation};
         --line-indentation: -${linePosition}rem;
        ${getFontPropsVarString(fontProps)}`)
</script>

<ul class="tree" style={styleString}>
	{#each actualLevelNodes as node (node.id)}
		{#if children}
			<!-- children snippet overrides the whole node render -->
			{@render children(node)}
		{:else}
			<!--this is the default content for the treeView: arrow, label and the other snippets -->
			<li
				class="node"
				class:leaf={!node.childrenIds || (node.childrenIds?.length === 0 && node.level > 0)}
				class:root={node.level === 0}
				class:rootLeaf={(!node.childrenIds || node.childrenIds?.length === 0) &&
					node.parentId === 'root' &&
					node.level === 0}
				style={styleString}
				open={(expansionStates.get(treeExpansionMapKey) ?? []).includes(node.id)}
			>
				<div class="node-details">
					<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
					<!-- non-interactive arrow kept as a styled div to preserve the tree's visual output -->
					<div
						class="node-arrow"
						class:nodeExpanded={(expansionStates.get(treeExpansionMapKey) ?? []).includes(node.id)}
						onclick={() => toggleExpansion(node.id, node.childrenIds?.length === 0)}
					></div>
					<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
					<!-- label kept as a span so line-clamp and the .node-label selectors stay unchanged -->
					<span
						class="node-label"
						title={node.label}
						class:active={activeNode &&
							activeNode.id === node.id &&
							activeNode.level === node.level}
						onclick={(event) => {
							event.preventDefault()
							onLabelClick?.(node)
						}}
						>{node.label}
					</span>
					{#if nodeAction}
						<div class="nodeActionDiv">
							{@render nodeAction(node)}
						</div>
					{/if}
					{#if nodeData}
						{@render nodeData(node)}
					{/if}
				</div>
				{#if node.childrenIds && node.childrenIds.length > 0 && (expansionStates.get(treeExpansionMapKey) ?? []).includes(node.id)}
					<Self
						nodes={findChildrenAndAllOfLevelsBelow(node)}
						{navigationDisabled}
						{indentation}
						{fontProps}
						{vElementPadding}
						{treeExpansionMapKey}
						{activeNode}
						{expansionStates}
						actualDepth={actualDepth + 1}
						{onLabelClick}
						{nodeAction}
						{nodeData}
					/>
				{/if}
			</li>
		{/if}
	{/each}
</ul>

<style lang="scss">
	@import '$styles/global';

	ul.tree {
		margin-bottom: 0;
		list-style: none;
		padding-left: var(--indentation);
	}

	:focus-visible {
		/* hides strange outline when double-clicking on details or summary item and pressing buttons */
		outline: none !important;
	}

	summary {
		display: block;
		cursor: default;

		&::marker,
		&::-webkit-details-marker {
			display: none;
		}
	}

	li.root {
		&::after,
		&::before {
			/* hide the lines for the root elements */
			display: none;
		}

		&:first-of-type {
			border-top: none;
			padding-top: 0;
		}
	}

	span.node-label.active {
		color: $primaryGreen !important;
	}

	li.rootLeaf {
		.node-arrow {
			display: none; /* for leaves do not display the arrow */
		}

		.node-label {
			margin-left: 12px; /* for leaves we need margin because arrow is not displayed */
		}
	}

	li.leaf {
		summary {
			cursor: text;
		}

		.node-arrow {
			display: none; /* for leaves do not display the arrow */
		}

		.node-label {
			margin-left: 12px; /* for leaves we need margin because arrow is not displayed */
		}

		&:not(.root):first-of-type::after {
			display: block !important; // display the vertical line for leaves, but not for rootLeaves
		}

		&:last-of-type::after {
			bottom: unset;
			top: -10px;
			height: 60%; /* half of 120% */
		}
	}

	li.node {
		position: relative;
		border-top: 1px solid $grey-600;

		&::before {
			/* horizontal line and parts of the vertical line until the top of the element except for the root */
			position: absolute;
			left: var(--line-indentation);
			top: 0;
			border-left: 2px solid $grey-750; // TODO switch this color once there is an active edit in this path
			border-bottom: 2px solid $grey-750;
			content: '';
			width: 10px;
			height: var(
				--horizontal-line-pos-y
			); /* TODO needs to be 50% of the height of one element at the end */
		}

		&::after {
			/* connects the vertical lines by overlaying another vertical line except for the root */
			position: absolute;
			left: var(--line-indentation);
			bottom: 0;
			top: 0;
			border-left: 2px solid $grey-750;
			content: '';
			width: 10px;
			height: 120%;
		}

		&:last-child::after {
			display: none;
		}

		& .node-details {
			@include flex-row(0.25rem, $justify: flex-start);

			padding: var(--vPadding) 0; /* element padding */
			height: var(--itemHeight);

			&:hover > .nodeActionDiv {
				opacity: 1;
			}

			&.nodeExpanded {
				max-height: unset;
			}
		}

		& .node-label {
			@include roboto-font(var(--line-height), var(--font-weight), var(--font-size), var(--color));

			cursor: pointer;

			&:hover {
				color: $primaryGreen;
				font-weight: 500;
			}
		}

		& .node-arrow {
			min-width: 20px;
			height: 20px;
			background: url('@/icons/icon_arrow_down.svg') no-repeat center;
			filter: invert(100%); /* TODO map check this, color is inverted */
			cursor: pointer;
			transition: transform 0.25s ease-out;

			&:hover {
				background: url('@/icons/icon_arrow_down_green.svg') no-repeat center;
			}

			&.nodeExpanded {
				transition: transform 0.25s ease-out;
				transform: rotate(-180deg);
			}
		}
	}

	.node-label {
		display: -webkit-box;
		-webkit-line-clamp: 3;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.nodeActionDiv {
		opacity: 0;
		display: block;
	}
</style>
